import { seg, tween } from '../../../core/effect/motion';
import {
  add, faceArt, place, playerColor, sprite,
} from './common';
import {
  LINK_URL, PLATE, PLATE_URL, linkBox, paintPlate, plateReady, plateSpec, stateOf,
} from './chainPlate';

/**
 * **チェーンの列の部品**（Issue #568。チェーンに積まれる `effects/chained.js` と処理の始まり `effects/chain_solving.js` が共有する）。
 * three は import しない（`ctx.THREE` / `ctx.kit` を受け取る）。並べ方は `./chainPlate.js::centerColumn`。
 */

/** 前の札が列に出る時間（上から順に `APPEAR_STEP` ずつ遅れて） */
export const APPEAR = 120;
export const APPEAR_STEP = 25;

/** 前の札の出方（0 → 1。`index` は列の添字） */
export const appearAt = (t, index) => tween(t, index * APPEAR_STEP, index * APPEAR_STEP + APPEAR, 'expo_out');
export const appearAlpha = (t, index) => seg(t, index * APPEAR_STEP, index * APPEAR_STEP + APPEAR * 0.6);

/**
 * **札 1 枚**（canvas で描いてテクスチャにした板 ＋ 叩きつけの閃光 ＋ 灰色の板）。
 * 灰色の板は効果の無効の札が沈むとき（`chain_solving`）に重ねる（同じテクスチャを彩度 0・暗く）。
 * `refresh()` は画像が後から読めたら描き直す（update から毎フレーム呼ぶ）。
 */
export function makePlate(ctx, link, order, parent) {
  const { THREE, kit } = ctx;
  const g = new THREE.Group();
  add(parent, g, order);
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (c) { c.width = PLATE.w; c.height = PLATE.h; }
  const tex = c ? kit.textureFromCanvas(c) : null;
  const mesh = kit.quad(kit.plain(tex, 1), PLATE.w, PLATE.h);
  mesh.renderOrder = order;
  g.add(mesh);
  const grey = kit.quad(tex ? kit.cardArtMaterial(tex, { saturation: 0, brightness: 0.5 }) : kit.plain(null, 1), PLATE.w, PLATE.h);
  if (!tex) grey.material.color?.set?.('#3a3a3a');
  grey.renderOrder = order + 0.2;
  g.add(grey);
  const flash = sprite(ctx, kit.urlTexture(PLATE_URL), PLATE.w, PLATE.h, { blend: 'add' });
  flash.renderOrder = order + 0.5;
  g.add(flash);
  const imgs = { art: faceArt(ctx, { code: link.code, player: link.player }) };
  const pl = {
    link, g, mesh, grey, flash, tex, imgs, ready: '', at: null,
  };
  pl.refresh = () => {
    const key = JSON.stringify([stateOf(link), ...plateReady(imgs)]);
    if (key === pl.ready) return;
    pl.ready = key;
    const g2 = c?.getContext?.('2d');
    if (!g2) return;
    g2.setTransform(1, 0, 0, 1, 0, 0);
    g2.clearRect(0, 0, c.width, c.height);
    paintPlate(g2, plateSpec(link, { color: playerColor(ctx, link.player) }), imgs);
    if (tex) tex.needsUpdate = true;
  };
  return pl;
}

/** 札の中の点（札の座標。中心が原点）→ 舞台のピクセル。`p` = `{ x, y, s, sy, rot }` */
export function toStage(p, [lx, ly]) {
  const x = lx * p.s; const y = ly * p.sy;
  const c = Math.cos(p.rot); const sn = Math.sin(p.rot);
  return [p.x + x * c - y * sn, p.y + x * sn + y * c];
}

/** 鎖のつなぎの板 */
export const makeJoin = (ctx, parent, order) => add(parent, sprite(ctx, ctx.kit.urlTexture(LINK_URL), 1, 1), order);

/**
 * **鎖のつなぎを置く**（上の札の仕切りの下から、下の札の仕切りの上へ。札がずれていれば斜めに掛かる）。
 * @returns 縦の長さ（0 以下なら掛からない）
 */
export function placeJoin(m, up, lo) {
  const lb = linkBox();
  const over = PLATE.h - lb.y;
  const cxL = lb.x + lb.w / 2 - PLATE.w / 2;
  const a = toStage(up, [cxL, lb.y - PLATE.h / 2]);
  const b = toStage(lo, [cxL, over - PLATE.h / 2]);
  const dx = b[0] - a[0]; const dy = b[1] - a[1];
  place(m, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2, {
    sx: lb.w * (up.s + lo.s) / 2, sy: Math.max(0.001, Math.hypot(dx, dy)), rot: Math.atan2(-dx, dy),
  });
  return dy;
}

/** 箱（左上と幅）→ 中心と倍率 */
export const centerOf = (b) => ({ x: b.x + b.w / 2, y: b.y + b.h / 2, s: b.scale, rot: b.rot ?? 0 });

/** 積まれているリンク（このリンクまで。`{ chain, code, player, negated, disabled }`。チェーンの番号の順） */
export function linksUpTo(payload) {
  const n = Number(payload?.link?.chain ?? payload?.chain) || 0;
  return (payload?.chainLinks ?? [])
    .filter((l) => l.chain <= n)
    .map((l) => ({
      chain: l.chain, code: l.code ?? null, player: l.player === 1 ? 1 : 0, negated: Boolean(l.negated), disabled: Boolean(l.disabled),
    }));
}
